import React, { useState } from 'react';
import { List, useTable, EditButton, DeleteButton } from '@refinedev/antd';
import { useInvalidate } from '@refinedev/core';
import { Table, Space, Tag, Button, Modal, Form, Input, InputNumber, App as AntApp, Popconfirm } from 'antd';
import { PlusOutlined, ImportOutlined } from '@ant-design/icons';
import { API_BASE_URL, axiosInstance } from '../../providers/dataProvider';

interface GymBranchRow {
  id: string;
  name: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  geofenceRadiusM: number;
  isActive: boolean;
  gym: { id: string; name: string };
}

interface ParsedSheetRow {
  rawCategoryOrBrand?: string;
  rawName: string;
  notes?: string;
}

/** Parse pasted sheet rows: "Brand \t Name [\t Quantity/Notes]" per line. */
function parseSheetText(text: string): ParsedSheetRow[] {
  const rows: ParsedSheetRow[] = [];
  for (const line of text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)) {
    const [rawCategoryOrBrand, rawName, rest] = line.split(/\t|\s*\|\s*/);
    if (!rawName?.trim()) continue;
    rows.push({
      rawCategoryOrBrand: rawCategoryOrBrand?.trim() || undefined,
      rawName: rawName.trim(),
      notes: rest?.trim() || undefined,
    });
  }
  return rows;
}

export const GymBranchList: React.FC = () => {
  const { message } = AntApp.useApp();
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importTarget, setImportTarget] = useState<GymBranchRow | null>(null);
  const [form] = Form.useForm();
  const [importForm] = Form.useForm();

  const { tableProps } = useTable<GymBranchRow>({
    resource: 'gym-branches',
    pagination: { current: 1, pageSize: 50 },
  });
  const invalidate = useInvalidate();
  const refresh = () => invalidate({ resource: 'gym-branches', invalidates: ['list'] });

  const openImportFor = (record: GymBranchRow) => {
    setImportTarget(record);
    importForm.setFieldsValue({ branchId: `${record.gym?.name ?? ''} — ${record.name}` });
    setImportOpen(true);
  };

  const submitImport = async () => {
    const { sheet } = importForm.getFieldsValue();
    const rows = parseSheetText(sheet ?? '');
    if (!importTarget || rows.length === 0) {
      message.error('No parsable rows — keep each line as: Brand<TAB>Equipment name<TAB>Quantity/Notes');
      return;
    }
    try {
      const res = await axiosInstance.post(`${API_BASE_URL}/gym-branches/import`, {
        gymName: importTarget.gym.name,
        branchName: importTarget.name,
        latitude: importTarget.latitude ?? undefined,
        longitude: importTarget.longitude ?? undefined,
        rows,
      });
      const r = res.data;
      message.success(
        `Imported ${r.total} rows: ${r.mapped} mapped, ${r.autoCreated} auto-created, ` +
          `${r.offsite} offsite, ${r.failed} failed`,
      );
      setImportOpen(false);
      importForm.resetFields();
      refresh();
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Import failed');
    }
  };

  const submitCreate = async () => {
    const values = await form.validateFields();
    try {
      await axiosInstance.post(`${API_BASE_URL}/gym-branches`, values);
      message.success('Branch created');
      setCreateOpen(false);
      form.resetFields();
      refresh();
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Create failed');
    }
  };

  return (
    <List
      resource="gym-branches"
      headerButtons={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          New branch
        </Button>
      }
    >
      <Table {...tableProps} rowKey="id">
        <Table.Column dataIndex="gym" title="Gym" render={(_: any, r: GymBranchRow) => r.gym?.name} />
        <Table.Column dataIndex="name" title="Branch" />
        <Table.Column
          title="Coordinates"
          render={(_: any, r: GymBranchRow) =>
            r.latitude != null && r.longitude != null
              ? `${r.latitude.toFixed(4)}, ${r.longitude.toFixed(4)}`
              : '—'
          }
        />
        <Table.Column dataIndex="geofenceRadiusM" title="Radius (m)" width={100} />
        <Table.Column
          dataIndex="isActive"
          title="Active"
          width={90}
          render={(v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? 'Active' : 'Off'}</Tag>}
        />
        <Table.Column
          title="Actions"
          width={200}
          render={(_: any, r: GymBranchRow) => (
            <Space>
              <Button size="small" icon={<ImportOutlined />} onClick={() => openImportFor(r)}>
                Import sheet
              </Button>
              <EditButton hideText size="small" recordItemId={r.id} resource="gym-branches" />
              <Popconfirm
                title="Deactivate this branch?"
                onConfirm={async () => {
                  await axiosInstance.delete(`${API_BASE_URL}/gym-branches/${r.id}`);
                  message.success('Branch deactivated');
                  refresh();
                }}
              >
                <DeleteButton hideText size="small" recordItemId={r.id} resource="gym-branches" />
              </Popconfirm>
            </Space>
          )}
        />
      </Table>

      <Modal
        open={createOpen}
        title="New gym branch"
        onOk={submitCreate}
        onCancel={() => setCreateOpen(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="gymName" label="Gym chain name" rules={[{ required: true }]}>
            <Input placeholder="EFX 24" />
          </Form.Item>
          <Form.Item name="name" label="Branch name" rules={[{ required: true }]}>
            <Input placeholder="Siu Sai Wan" />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input />
          </Form.Item>
          <Space>
            <Form.Item name="latitude" label="Latitude" rules={[{ required: true }]}>
              <InputNumber step={0.0001} />
            </Form.Item>
            <Form.Item name="longitude" label="Longitude" rules={[{ required: true }]}>
              <InputNumber step={0.0001} />
            </Form.Item>
            <Form.Item name="geofenceRadiusM" label="Radius (m)" initialValue={1000}>
              <InputNumber min={50} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      <Modal
        open={importOpen}
        title={`Import equipment sheet — ${importTarget?.gym?.name ?? ''} ${importTarget?.name ?? ''}`}
        onOk={submitImport}
        onCancel={() => setImportOpen(false)}
        width={720}
        destroyOnClose
      >
        <Form form={importForm} layout="vertical">
          <Form.Item name="branchId" label="Branch">
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="sheet"
            label="Paste sheet rows — one per line: Brand / Category, Tab, Equipment name, Tab, Quantity / Notes"
            rules={[{ required: true }]}
          >
            <Input.TextArea
              rows={12}
              placeholder={'PANATTA\tSTANDING MULTI FLIGHT\nCARDIO EQUIPMENT\tTREADMILL (ASPIRE LF)\t5\nPANATTA\tSUPER LEG PRESS 45C DUAL SYSTEM\ttransfer to other site'}
            />
          </Form.Item>
        </Form>
      </Modal>
    </List>
  );
};
